import { type FC, useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button, ProgressMessage } from '@/components/ui';
import { supabase } from '@/lib/api/supabase';
import type { Tournament, Addon, Cart } from '@/types';
import { ShoppingCart, CreditCard, AlertCircle, ChevronLeft, Loader2, Clock } from 'lucide-react';
import {
  generateOrderIdempotencyKey,
  hashCart,
  storeIdempotencyKey,
  getOrderIdFromIdempotencyKey,
} from '@/lib/utils/idempotency';
import { DiscountCodeInput, type DiscountInfo } from './DiscountCodeInput';
import { validateDiscountCode, getDiscountAmount } from '@/lib/utils/discountCode';
import { buildOrderItemVariantData } from '../utils/registrationEntryState';

interface CheckoutStepProps {
  cart: Cart;
  tournaments: Tournament[];
  addons: Addon[];
  eventRegistrationFee: number;
  eventRegistrationName: string;
  onBack: () => void;
  onComplete: (orderId: string) => void;
}

/**
 * Fourth step of registration wizard - Checkout
 * Reviews cart contents and processes payment via Stripe
 */
export const CheckoutStep: FC<CheckoutStepProps> = ({
  cart,
  tournaments,
  addons,
  eventRegistrationFee,
  eventRegistrationName,
  onBack,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountInfo | null>(null);
  const [showProgressMessage, setShowProgressMessage] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Processing your order...');

  // Ref for immediate double-click protection (no React state delay)
  const isSubmittingRef = useRef(false);

  // Show progress message after 3 seconds of loading
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (loading) {
      timer = setTimeout(() => {
        setShowProgressMessage(true);
      }, 3000);
    } else {
      setShowProgressMessage(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const handleApplyDiscount = async (code: string) => {
    setDiscountLoading(true);
    setError(null);

    try {
      const cartItemsForValidation = cart.items.map(item => ({
        type: item.type,
        itemId: item.itemId,
      }));
      const result = await validateDiscountCode(code, cartItemsForValidation);

      if (result.isValid && result.discount) {
        setAppliedDiscount(result.discount);
      } else {
        setError(result.error || 'Invalid discount code');
      }
    } catch (err) {
      console.error('Error applying discount:', err);
      setError('Failed to apply discount code. Please try again.');
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setError(null);
  };

  const handleCheckout = async (): Promise<void> => {
    // Immediate blocking - prevents race condition from multiple rapid clicks
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    let createdOrderId: string | null = null;
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('You must be logged in to complete checkout');
      }

      // Generate idempotency key based on user and cart contents
      setProgressMessage('Preparing your order...');
      const cartHash = await hashCart(cart);
      const idempotencyKey = generateOrderIdempotencyKey(user.id, cartHash);

      // Check if we already have an order for this cart
      const existingOrderId = getOrderIdFromIdempotencyKey(idempotencyKey);
      if (existingOrderId) {
        // Verify the order still exists in the database
        const { data: existingOrder, error: checkError } = await supabase
          .from('orders')
          .select('id, stripe_checkout_session_id')
          .eq('id', existingOrderId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (!checkError && existingOrder) {
          // Order exists, check if we have a Stripe session
          if (existingOrder.stripe_checkout_session_id) {
            // Try to retrieve the Stripe session to get the URL
            const { data: session, error: stripeError } = await supabase.functions.invoke(
              'create-checkout-session',
              {
                body: { orderId: existingOrder.id },
              }
            );

            if (!stripeError && session?.url) {
              // Redirect to existing Stripe session
              window.location.href = session.url;
              return;
            }
          }
          // If we can't get the Stripe session, we'll create a new one below
        }
        // If order doesn't exist or there was an error, continue to create new order
      }

      // Validate tournament capacity before proceeding (including reserved spots)
      setProgressMessage('Validating tournament availability...');
      const tournamentItems = cart.items.filter((item) => item.type === 'tournament');
      if (tournamentItems.length > 0) {
        const tournamentIds = tournamentItems.map((item) => item.itemId);
        const { data: currentTournaments, error: fetchError } = await supabase
          .from('tournaments')
          .select('id, name, current_participants, reserved_participants, max_participants')
          .in('id', tournamentIds);

        if (fetchError) {
          throw new Error('Failed to verify tournament availability');
        }

        // Check if tournaments are full (considering both confirmed and reserved spots)
        const fullTournaments = currentTournaments?.filter((t) => {
          const effectiveUsed = t.current_participants + (t.reserved_participants || 0);
          return effectiveUsed >= t.max_participants;
        });

        if (fullTournaments && fullTournaments.length > 0) {
          const tournamentNames = fullTournaments.map((t) => t.name).join(', ');
          throw new Error(
            `The following tournament(s) are now full: ${tournamentNames}. Please go back and adjust your selection.`
          );
        }
      }

      // Validate add-on inventory before proceeding (including reserved quantities)
      setProgressMessage('Validating add-on availability...');
      const addonCartItems = cart.items.filter((item) => item.type === 'addon');
      if (addonCartItems.length > 0) {
        const addonIds = addonCartItems.map((item) => item.itemId);
        const { data: currentAddons, error: addonFetchError } = await supabase
          .from('addons')
          .select('id, name, has_inventory, stock_quantity, reserved_quantity, max_per_order')
          .in('id', addonIds);

        if (addonFetchError) {
          throw new Error('Failed to verify add-on availability');
        }

        // Check each addon in cart against current inventory
        const inventoryIssues: string[] = [];
        for (const cartItem of addonCartItems) {
          const addon = currentAddons?.find((a) => a.id === cartItem.itemId);
          if (!addon) continue;

          // Check if addon has inventory tracking enabled
          if (addon.has_inventory && addon.stock_quantity !== null) {
            const reservedQty = addon.reserved_quantity || 0;
            const effectiveAvailable = addon.stock_quantity - reservedQty;

            if (effectiveAvailable <= 0) {
              inventoryIssues.push(`"${addon.name}" is now out of stock`);
            } else if (cartItem.quantity > effectiveAvailable) {
              inventoryIssues.push(
                `"${addon.name}" only has ${effectiveAvailable} available (you selected ${cartItem.quantity})`
              );
            }
          }

          // Check max per order limit
          if (addon.max_per_order !== null && cartItem.quantity > addon.max_per_order) {
            inventoryIssues.push(
              `"${addon.name}" has a limit of ${addon.max_per_order} per order (you selected ${cartItem.quantity})`
            );
          }
        }

        if (inventoryIssues.length > 0) {
          throw new Error(
            `Inventory issues: ${inventoryIssues.join('; ')}. Please go back and adjust your selections.`
          );
        }
      }

      // Create order in database
      setProgressMessage('Creating your order...');
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          user_id: user.id,
          payment_status: 'pending',
          subtotal: cart.subtotal,
          tax: cart.tax,
          total: cart.total, // Final total calculated in Stripe checkout
          fulfillment_status: 'pending',
        })
        .select()
        .single();

      if (orderError || !order) {
        throw new Error('Failed to create order');
      }
      createdOrderId = order.id;

      // Store idempotency key for this order
      storeIdempotencyKey(idempotencyKey, order.id);

      // Create order items.
      // Event registration items always start unlinked: the stripe-webhook
      // creates one event_registrations row per entry item after payment and
      // back-fills event_registration_id / item_id. The nil UUID is a
      // placeholder (item_id is NOT NULL). Server-side guards in
      // create-checkout-session reject entry items for users who already hold
      // a weekend registration, so no pre-linking is needed here.
      const NIL_UUID = '00000000-0000-0000-0000-000000000000';
      const orderItems = cart.items.map((item) => ({
        order_id: order.id,
        item_id: item.type === 'event_registration' ? NIL_UUID : item.itemId,
        item_type: item.type,
        event_registration_id: null,
        tournament_registration_id: null, // Created later when user fills out tournament details
        activity_registration_id: null, // Created later when user fills out activity details
        addon_id: item.type === 'addon' ? item.itemId : null,
        special_event_registration_id: null, // Created later when needed
        quantity: item.quantity,
        unit_price: item.price,
        subtotal: item.price * item.quantity,
        tax: 0, // Tax calculation can be added per-item later if needed
        total: item.price * item.quantity,
        discount_code: appliedDiscount?.code || null,
        discount_amount: appliedDiscount ? getDiscountAmount(item.price * item.quantity, appliedDiscount) : 0,
        item_name: item.name,
        item_description: item.description,
        variant_data: buildOrderItemVariantData(item),
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) {
        throw new Error('Failed to create order items');
      }

      // Create Stripe checkout session via edge function
      setProgressMessage('Setting up secure payment...');
      const { data: session, error: stripeError } = await supabase.functions.invoke(
        'create-checkout-session',
        {
          body: {
            orderId: order.id,
            promotionCodeId: appliedDiscount?.promotionCodeId || null,
          },
        }
      );

      if (stripeError || !session?.url) {
        // Extract error message from edge function response if available
        const edgeFunctionError = session?.error || stripeError?.message || 'Failed to create checkout session';
        throw new Error(edgeFunctionError);
      }

      // Redirect to Stripe checkout
      setProgressMessage('Redirecting to secure payment...');
      window.location.href = session.url;
    } catch (err) {
      console.error('Checkout error:', err);

      // Clean up the pending order if it was created but Stripe session failed
      if (createdOrderId) {
        try {
          await supabase.from('order_items').delete().eq('order_id', createdOrderId);
          await supabase.from('orders').delete().eq('id', createdOrderId).eq('payment_status', 'pending');
        } catch (cleanupErr) {
          console.error('Failed to clean up pending order:', cleanupErr);
        }
      }

      // Enhanced error messaging
      let errorMessage = 'Failed to process checkout';
      
      if (err instanceof Error) {
        // Network error handling with retry instructions
        if (
          err.message.includes('Failed to fetch') ||
          err.message.includes('Network request failed') ||
          err.message.includes('NetworkError') ||
          err.message.includes('Connection timeout')
        ) {
          errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } 
        // Session timeout handling
        else if (err.message.includes('logged in')) {
          errorMessage = 'Your session has expired. Please log in again to complete your registration.';
        }
        // Capacity errors (already have good messages from validation)
        else if (err.message.includes('full')) {
          errorMessage = err.message;
        }
        // Inventory/stock errors from server-side validation
        else if (
          err.message.includes('stock') ||
          err.message.includes('Inventory') ||
          err.message.includes('reserve') ||
          err.message.includes('available')
        ) {
          errorMessage = err.message;
        }
        // Generic error with retry guidance
        else {
          errorMessage = err.message + ' Please try again or contact support if the problem persists.';
        }
      }
      
      setError(errorMessage);
      setLoading(false);
      // Reset ref to allow retry after error
      isSubmittingRef.current = false;
    }
  };

  const eventRegItems = cart.items.filter((item) => item.type === 'event_registration');
  const tournamentItems = cart.items.filter((item) => item.type === 'tournament');
  const addonItems = cart.items.filter((item) => item.type === 'addon');

  if (cart.items.length === 0) {
    return (
      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
        <CardContent className="py-12 text-center">
          <ShoppingCart className="h-12 w-12 text-orange-300 mx-auto mb-3" />
          <p className="text-orange-200 mb-4">Your cart is empty</p>
          <Button onClick={onBack} variant="outline" className="border-orange-500 text-orange-200 hover:bg-orange-500/20">
            Go Back
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
        <CardHeader>
          <CardTitle className="font-viking text-xl sm:text-2xl flex items-center gap-2 text-white">
            <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-orange-400" />
            Review Your Order
          </CardTitle>
          <p className="text-sm text-orange-200">
            Please review your selections before proceeding to payment
          </p>
        </CardHeader>
        <CardContent className="space-y-6" data-testid="cart-summary">
          {/* Event Registration (supporter entry, spectator pass, or day passes) */}
          {eventRegItems.length > 0 && (
            <div>
              <h3 className="font-semibold text-lg mb-3 text-orange-400">Event Registration</h3>
              <div className="space-y-2 sm:space-y-3">
                {eventRegItems.map((item) => (
                  <div key={item.id} className="bg-turquoise-700/50 rounded-lg p-3 sm:p-4 border border-turquoise-600" data-testid="cart-item">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <div>
                        <p className="font-medium text-white text-sm sm:text-base">{item.name || `Cactus Cup 2026 ${eventRegistrationName}`}</p>
                        {item.description && (
                          <p className="text-xs sm:text-sm text-orange-200">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <p className="font-bold text-orange-400 text-lg sm:text-base">${((item.price ?? eventRegistrationFee) / 100).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tournament Registrations */}
          {tournamentItems.length > 0 && (
            <div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 sm:mb-3 text-white">
                Tournament Registrations ({tournamentItems.length})
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {tournamentItems.map((item) => {
                  const tournament = tournaments.find((t) => t.id === item.itemId);
                  return (
                    <div key={item.itemId} className="bg-turquoise-700/50 rounded-lg p-3 sm:p-4 border border-turquoise-600" data-testid="cart-item">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-2">
                        <div>
                          <p className="font-medium text-white text-sm sm:text-base">{item.name}</p>
                          {tournament && (
                            <p className="text-xs sm:text-sm text-orange-200 capitalize">
                              {tournament.weapon} • {tournament.division}
                            </p>
                          )}
                        </div>
                        <p className="font-bold text-orange-400">${(item.price / 100).toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add-ons */}
          {addonItems.length > 0 && (
            <div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 sm:mb-3 text-white">
                Merchandise & Add-ons ({addonItems.length})
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {addonItems.map((item) => {
                  const addon = addons.find((a) => a.id === item.itemId);
                  const totalPrice = item.price * item.quantity;
                  return (
                    <div key={`${item.itemId}-${JSON.stringify(item.variant?.data)}`} className="bg-turquoise-700/50 rounded-lg p-3 sm:p-4 border border-turquoise-600" data-testid="cart-item">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-white text-sm sm:text-base">{item.name}</p>
                            <span className="text-orange-200 text-sm">× {item.quantity}</span>
                          </div>
                          {addon && addon.category && (
                            <p className="text-xs sm:text-sm text-orange-200 capitalize">
                              {addon.category}
                            </p>
                          )}
                          {item.variant?.data && (
                            <p className="text-xs sm:text-sm text-orange-200">
                              {Object.entries(item.variant.data)
                                .map(([key, value]) => `${key}: ${value}`)
                                .join(' • ')}
                            </p>
                          )}
                        </div>
                        <div className="text-left sm:text-right">
                          <p className="font-bold text-orange-400">${(totalPrice / 100).toFixed(2)}</p>
                          <p className="text-xs text-orange-200/80">
                            ${(item.price / 100).toFixed(2)} each
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Discount Code Input */}
          <div className="border-t border-turquoise-600 pt-4 sm:pt-6">
            <DiscountCodeInput
              onApply={handleApplyDiscount}
              onRemove={handleRemoveDiscount}
              appliedCode={appliedDiscount?.code}
              appliedDiscount={appliedDiscount || undefined}
              isLoading={discountLoading}
            />
          </div>

          {/* Order Summary */}
          <div className="border-t border-turquoise-600 pt-4 sm:pt-6">
            <div className="flex justify-between items-center text-lg sm:text-xl font-bold text-white">
              <span>Total</span>
              <span data-testid="cart-total" className="text-orange-400 italic">View total in checkout</span>
            </div>
          </div>

          {/* Progress Message for long operations */}
          {loading && showProgressMessage && (
            <div className="rounded-lg bg-turquoise-700/50 p-4 border border-orange-500/40">
              <ProgressMessage 
                message={progressMessage}
                estimatedSeconds={5}
                showTimer={true}
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div 
              className="rounded-lg bg-red-500/20 p-4 text-red-300 flex items-start gap-2 border border-red-500/40"
              role="alert"
              aria-live="assertive"
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-semibold">Checkout Error</p>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Inventory Hold Information */}
          <div className="rounded-lg bg-turquoise-700/30 p-4 border border-turquoise-600/50">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-turquoise-600/50 p-2 flex-shrink-0">
                <Clock className="h-4 w-4 text-turquoise-300" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-medium text-turquoise-200">Limited Inventory Protection</p>
                <p className="text-xs text-turquoise-300 mt-1">
                  When you proceed to payment, your selected items will be held for you for a limited time.
                  If you don't complete payment, items may become available to other customers.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onBack}
              disabled={loading}
              className="w-full sm:flex-1 border-orange-500 text-orange-200 hover:bg-orange-500/20 order-2 sm:order-1"
              aria-label="Go back to previous step"
            >
              <ChevronLeft className="h-4 w-4 mr-1" aria-hidden="true" />
              Back
            </Button>
            <Button
              onClick={handleCheckout}
              disabled={loading}
              variant="secondary"
              className="w-full sm:flex-1 order-1 sm:order-2"
              aria-label={loading ? 'Processing payment' : 'Proceed to secure payment'}
              aria-busy={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" aria-hidden="true" />
                  Proceed to Payment
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-center text-orange-200/80">
            You will be redirected to Stripe for secure payment processing
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
